/**
 * Falling Tickets Animation Background
 *
 * Creates an animated background of falling pull tab tickets for banner use.
 * Designed to be subtle and not interfere with foreground logo content.
 */

import { useEffect, useState, useCallback } from 'react';

interface TicketProps {
  delay: number;
  duration: number;
  left: number;
  rotation: number;
  size: number;
}

const Ticket = ({ delay, duration, left, rotation, size }: TicketProps) => (
  <div
    className="absolute animate-fall"
    style={{
      left: `${left}%`,
      animationDelay: `${delay}s`,
      animationDuration: `${duration}s`,
      '--rotation': `${rotation}deg`,
      '--scale': size * 3, // Triple the size via CSS variable (25% reduction from 4x)
      opacity: 0, // Start invisible - animation will control visibility
    } as React.CSSProperties}
  >
    {/* Pull tab ticket SVG - 4 tabs on long side */}
    <svg
      width="40"
      height="28"
      viewBox="0 0 40 28"
    >
      {/* Ticket body */}
      <rect
        x="4"
        y="0"
        width="36"
        height="20"
        rx="2"
        fill="white"
        stroke="#10B981"
        strokeWidth="1"
        opacity="0.6"
      />

      {/* Four pull tabs along the bottom edge */}
      <rect
        x="6"
        y="20"
        width="6"
        height="8"
        rx="1"
        fill="white"
        stroke="#10B981"
        strokeWidth="1"
        opacity="0.6"
      />
      <rect
        x="14"
        y="20"
        width="6"
        height="8"
        rx="1"
        fill="white"
        stroke="#10B981"
        strokeWidth="1"
        opacity="0.6"
      />
      <rect
        x="22"
        y="20"
        width="6"
        height="8"
        rx="1"
        fill="white"
        stroke="#10B981"
        strokeWidth="1"
        opacity="0.6"
      />
      <rect
        x="30"
        y="20"
        width="6"
        height="8"
        rx="1"
        fill="white"
        stroke="#10B981"
        strokeWidth="1"
        opacity="0.6"
      />

      {/* Seven equally spaced green bars centered on ticket */}
      <rect x="9" y="2" width="1.5" height="16" fill="#10B981" opacity="0.45" />
      <rect x="13" y="2" width="1.5" height="16" fill="#10B981" opacity="0.45" />
      <rect x="17" y="2" width="1.5" height="16" fill="#10B981" opacity="0.45" />
      <rect x="21" y="2" width="1.5" height="16" fill="#10B981" opacity="0.45" />
      <rect x="25" y="2" width="1.5" height="16" fill="#10B981" opacity="0.45" />
      <rect x="29" y="2" width="1.5" height="16" fill="#10B981" opacity="0.45" />
      <rect x="33" y="2" width="1.5" height="16" fill="#10B981" opacity="0.45" />
    </svg>
  </div>
);

interface FallingTicketsProps {
  className?: string;
  density?: 'light' | 'medium' | 'heavy';
  speed?: 'slow' | 'medium' | 'fast';
}

export default function FallingTickets({
  className = '',
  density = 'medium',
  speed = 'medium'
}: FallingTicketsProps) {
  const [tickets, setTickets] = useState<TicketProps[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);

  const generateTickets = useCallback(() => {
    const densityMap = { light: 8, medium: 15, heavy: 25 };
    const speedMap = { slow: [8, 15], medium: [6, 12], fast: [4, 8] };

    const ticketCount = densityMap[density];
    const [minDuration, maxDuration] = speedMap[speed];

    return Array.from({ length: ticketCount }, (_, i) => ({
      delay: Math.random() * 10, // Stagger start times
      duration: minDuration + Math.random() * (maxDuration - minDuration),
      left: Math.random() * 100, // Random horizontal position
      rotation: Math.random() * 360, // Random rotation
      size: 0.6 + Math.random() * 0.8, // Random size (0.6x - 1.4x)
    }));
  }, [density, speed]);

  useEffect(() => {
    // Delay animation initialization to allow page to render first
    const timeoutId = setTimeout(() => {
      // Use requestAnimationFrame to ensure this happens after paint
      requestAnimationFrame(() => {
        const newTickets = generateTickets();
        setTickets(newTickets);
        setIsLoaded(true);
      });
    }, 100); // Small delay to let page render

    return () => clearTimeout(timeoutId);
  }, [generateTickets]);

  // Don't render anything until lazy load is complete
  if (!isLoaded) {
    return null;
  }

  return (
    <div className={`fixed inset-0 pointer-events-none overflow-hidden ${className}`}>
      {tickets.map((ticket, index) => (
        <Ticket
          key={index}
          {...ticket}
        />
      ))}
    </div>
  );
}